import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useAnchoredPosition } from "hooks/useAnchoredPosition";
import { HighlightedText } from "components/common/HighlightedText";
import { TooltipSurface } from "components/common/TooltipSurface";
import { resolveTooltipAnchoring } from "components/common/TooltipSurface/resolveTooltipAnchoring";
import type {
  TruncatedTextTooltipPosition,
  TruncatedTextTooltipProps,
} from "./types";

const TOOLTIP_MAX_WIDTH = 360;
const VIEWPORT_MARGIN = 8;

export const TruncatedTextTooltip = ({
  text,
  className,
  highlightQuery = "",
}: TruncatedTextTooltipProps) => {
  const textRef = useRef<HTMLSpanElement>(null);

  const [isHovered, setIsHovered] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  const isTooltipOpen = isHovered && isTruncated;

  const handleMouseEnter = useCallback(() => {
    const element = textRef.current;
    if (element) setIsTruncated(element.scrollWidth > element.clientWidth);
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const position = useAnchoredPosition<TruncatedTextTooltipPosition>({
    isOpen: isTooltipOpen,
    anchorRef: textRef,
    compute: (anchorRect) => {
      const { top, placement, anchorCenterX } = resolveTooltipAnchoring({
        anchorRect,
      });

      // Clamp against the right edge so a long name never runs off-screen.
      const maxLeft = window.innerWidth - VIEWPORT_MARGIN - TOOLTIP_MAX_WIDTH;
      const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(anchorRect.left, maxLeft),
      );

      return { top, left, placement, anchorCenterX };
    },
  });

  return (
    <>
      <span
        ref={textRef}
        className={clsx("truncate", className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <HighlightedText text={text} searchQuery={highlightQuery} />
      </span>
      {isTooltipOpen &&
        position &&
        createPortal(
          <TooltipSurface
            arrowSide={position.placement === "top" ? "bottom" : "top"}
            arrowOffsetLeft={position.anchorCenterX - position.left}
            className="z-50 animate-fade-in break-words"
            style={{
              top: `${position.top}px`,
              left: `${position.left + window.scrollX}px`,
              maxWidth: `${TOOLTIP_MAX_WIDTH}px`,
              transform:
                position.placement === "top" ? "translateY(-100%)" : undefined,
            }}
          >
            <HighlightedText text={text} searchQuery={highlightQuery} />
          </TooltipSurface>,
          document.body,
        )}
    </>
  );
};
